import { type FC, useCallback, useMemo, useState } from "react";

import type { PageMetadata } from "@moment/api-collab/api-types";
import { useDocumentFromCurrentRoute, useDocumentMetadata } from "@moment/state";
import { cn } from "@moment/ui";

import { PropertiesSection } from "../PropertiesSection";

export const INSERT_TLDRAW_EMBED_EVENT_ID = "moment-insert-tldraw-embed";

export type InsertTldrawEmbedDetail = {
	pageId: string;
	pageTitle: string;
};

export const TldrawEmbedSection: FC = () => {
	const { documentId } = useDocumentFromCurrentRoute();
	const { metadata } = useDocumentMetadata(documentId ?? "");

	const canvasPages = useMemo<PageMetadata[]>(() => {
		if (!metadata?.pagesMetadata) return [];
		return metadata.pagesMetadata.filter((page) => page.type === "canvas" && !page.archived);
	}, [metadata]);

	const hasCanvasPages = canvasPages.length > 0;
	const [selectedPageId, setSelectedPageId] = useState<string | null>(null);

	const handleInsert = useCallback((page: PageMetadata) => {
		document.dispatchEvent(
			new CustomEvent<InsertTldrawEmbedDetail>(INSERT_TLDRAW_EMBED_EVENT_ID, {
				detail: { pageId: page.id, pageTitle: page.title },
			})
		);
	}, []);

	return (
		<PropertiesSection title="TLDraw Canvas" collapseByDefault={false}>
			{!hasCanvasPages ? (
				<div className="text-tertiary text-xs italic py-1">
					No TLDraw canvas pages in this document.
				</div>
			) : (
				<div className="flex flex-col gap-1">
					{canvasPages.map((page) => (
						<button
							key={page.id}
							type="button"
							className={cn(
								"flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors",
								"hover:bg-primary",
								selectedPageId === page.id
									? "bg-primary ring-1 ring-brand"
									: "bg-transparent"
							)}
							onClick={() => {
								setSelectedPageId(page.id);
								handleInsert(page);
							}}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
								className="shrink-0 text-tertiary"
							>
								<path d="M12 20h9" />
								<path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z" />
							</svg>
							<span className="truncate">{page.title || "Untitled Canvas"}</span>
						</button>
					))}
				</div>
			)}
		</PropertiesSection>
	);
};
